(() => {
  if (customElements.get('b100-budget-collection')) return;
  const ceiling = 500;
  const sorts = {
    'best-selling': { sort: 'BEST_SELLING', reverse: false },
    newest: { sort: 'CREATED_AT', reverse: true },
    'price-ascending': { sort: 'PRICE', reverse: false },
    'price-descending': { sort: 'PRICE', reverse: true },
    'title-ascending': { sort: 'TITLE', reverse: false }
  };
  const query = `query BudgetProducts($after: String, $sort: ProductSortKeys!, $reverse: Boolean!, $country: CountryCode!) @inContext(country: $country) {
    products(first: 24, after: $after, sortKey: $sort, reverse: $reverse, query: "available_for_sale:true AND variants.price:>0 AND variants.price:<=5 AND NOT variants.price:>5") {
      nodes {
        id handle title onlineStoreUrl availableForSale
        featuredImage { url(transform: {maxWidth: 600}) }
        priceRange { minVariantPrice { amount currencyCode } maxVariantPrice { amount currencyCode } }
        variants(first: 2) { nodes { sku } }
      }
      pageInfo { hasNextPage endCursor }
    }
  }`;

  customElements.define('b100-budget-collection', class extends HTMLElement {
    connectedCallback() {
      this.events = new AbortController();
      this.grid = this.querySelector('[data-budget-grid]');
      this.status = this.querySelector('[data-budget-status]');
      this.error = this.querySelector('[data-budget-error]');
      this.more = this.querySelector('[data-budget-more]');
      this.sort = this.querySelector('select');
      this.template = this.querySelector('[data-budget-card]');
      this.seen = new Set();
      this.cursor = null;
      this.requestId = 0;
      this.hasMore = true;
      const initialSort = new URL(location.href).searchParams.get('budget_sort');
      this.sort.value = Object.hasOwn(sorts, initialSort) ? initialSort : 'best-selling';
      const signal = this.events.signal;
      this.querySelector('[data-budget-sort-form]').addEventListener('submit', event => {
        event.preventDefault();
        this.changeSort();
      }, { signal });
      this.sort.addEventListener('change', () => this.changeSort(), { signal });
      this.more.addEventListener('click', () => this.load(true), { signal });
      this.querySelector('[data-budget-retry]').addEventListener('click', () => this.load(true), { signal });
      this.load();
    }

    changeSort() {
      this.request?.abort();
      this.grid.replaceChildren();
      this.seen.clear();
      this.cursor = null;
      this.hasMore = true;
      const url = new URL(location.href);
      url.searchParams.set('budget_sort', this.sort.value);
      history.replaceState(history.state, '', url);
      this.load();
    }

    qualifies(product) {
      const min = product.priceRange?.minVariantPrice;
      const max = product.priceRange?.maxVariantPrice;
      const minCents = Math.round(Number(min?.amount) * 100);
      const maxCents = Math.round(Number(max?.amount) * 100);
      return product.availableForSale && product.onlineStoreUrl &&
        min?.currencyCode === this.dataset.currency && max?.currencyCode === this.dataset.currency &&
        Number.isFinite(minCents) && Number.isFinite(maxCents) && minCents > 0 &&
        minCents <= maxCents && maxCents <= ceiling;
    }

    async load(focusNew = false) {
      this.request?.abort();
      const requestId = ++this.requestId;
      this.request = new AbortController();
      this.grid.setAttribute('aria-busy', 'true');
      this.more.disabled = true;
      this.error.hidden = true;
      this.status.textContent = this.dataset.loading;
      let added = 0;
      let firstNew;
      try {
        // Skip any batches containing only ineligible mixed-price variants.
        // Every request keeps the server-side cap and every card is checked again.
        for (let batch = 0; batch < 5 && this.hasMore && added === 0; batch++) {
          const response = await fetch('/api/2026-07/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ query, variables: { after: this.cursor, country: this.dataset.country, ...(sorts[this.sort.value] || sorts['best-selling']) } }),
            signal: this.request.signal
          });
          if (!response.ok) throw new Error('Catalogue request failed');
          const payload = await response.json();
          const products = payload.data?.products;
          if (payload.errors?.length || !Array.isArray(products?.nodes) || !products.pageInfo) throw new Error('Invalid catalogue response');
          if (requestId !== this.requestId || !this.isConnected) return;
          const fragment = document.createDocumentFragment();
          for (const product of products.nodes) {
            if (!this.qualifies(product) || this.seen.has(product.id)) continue;
            const card = this.makeCard(product);
            if (!card) continue;
            firstNew ||= card.querySelector('.b100-product__title a');
            fragment.append(card);
            this.seen.add(product.id);
            added++;
          }
          this.grid.append(fragment);
          this.hasMore = products.pageInfo.hasNextPage;
          const nextCursor = products.pageInfo.endCursor;
          if (this.hasMore && (!nextCursor || nextCursor === this.cursor)) throw new Error('Invalid catalogue cursor');
          this.cursor = nextCursor;
        }
        this.status.textContent = this.seen.size || this.hasMore
          ? this.dataset.shown.replace('[count]', String(this.seen.size))
          : this.dataset.empty;
        if (focusNew) firstNew?.focus({ preventScroll: true });
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.requestId) return;
        this.error.hidden = false;
        this.status.textContent = this.seen.size ? this.dataset.shown.replace('[count]', String(this.seen.size)) : '';
      } finally {
        if (requestId === this.requestId && this.isConnected) {
          this.grid.setAttribute('aria-busy', 'false');
          this.more.disabled = false;
          this.more.hidden = !this.hasMore || !this.error.hidden;
        }
      }
    }

    makeCard(product) {
      const card = this.template.content.firstElementChild?.cloneNode(true);
      if (!card) return null;
      const id = product.id.split('/').pop();
      const href = `${this.dataset.root}products/${encodeURIComponent(product.handle)}`;
      card.dataset.productMaxPrice = Math.round(Number(product.priceRange.maxVariantPrice.amount) * 100);
      card.dataset.productId = id;
      card.querySelector('article').dataset.productId = id;
      const imageLink = card.querySelector('.b100-product__image');
      imageLink.href = href;
      imageLink.replaceChildren();
      if (product.featuredImage?.url) {
        const img = document.createElement('img');
        img.src = product.featuredImage.url;
        img.alt = '';
        img.width = 320;
        img.height = 320;
        img.loading = 'lazy';
        imageLink.append(img);
      } else {
        imageLink.append(this.querySelector('[data-budget-placeholder]').content.cloneNode(true));
      }
      const title = card.querySelector('.b100-product__title a');
      title.href = href;
      title.textContent = product.title;
      const variants = product.variants.nodes;
      const meta = card.querySelector('.b100-product__meta');
      meta.textContent = variants.length > 1 ? this.dataset.variants : variants[0]?.sku ? `${this.dataset.code} ${variants[0].sku}` : '';
      const availability = card.querySelector('.b100-product__availability');
      const dot = document.createElement('span');
      dot.setAttribute('aria-hidden', 'true');
      availability.dataset.available = 'true';
      availability.replaceChildren(dot, document.createTextNode(this.dataset.available));
      const price = card.querySelector('.price');
      const value = document.createElement('strong');
      value.className = 'price__current';
      value.textContent = new Intl.NumberFormat('el-GR', { style: 'currency', currency: this.dataset.currency }).format(Number(product.priceRange.minVariantPrice.amount));
      price.className = 'price';
      price.replaceChildren();
      if (Number(product.priceRange.minVariantPrice.amount) !== Number(product.priceRange.maxVariantPrice.amount)) {
        const from = document.createElement('span');
        from.className = 'price__from';
        from.textContent = this.dataset.from;
        price.append(from);
      }
      price.append(value);
      const action = this.querySelector('[data-budget-action]').content.firstElementChild.cloneNode(true);
      action.href = href;
      action.title = this.dataset.view;
      action.setAttribute('aria-label', `${this.dataset.view}: ${product.title}`);
      card.querySelector('.b100-product__action').replaceChildren(action);
      card.querySelector('spark-product-card')?.setAttribute('parent-id', id);
      return card;
    }

    disconnectedCallback() {
      this.events?.abort();
      this.request?.abort();
      this.requestId++;
    }
  });
})();
